/**
 * Salt the food entries leave out. Database foods are measured without added
 * salt, so a restaurant meal or salted home cooking never reaches the day's
 * sodium or the water-weight estimate. This builds a sodium-only diary entry.
 */
import { basicItemNutrients } from './foodNutrients';
import type { MealInsert, MealItem, MealType } from './types';

/** USDA FoodData Central 746775, "Salt, table, iodized": 38,700 mg sodium per 100 g. */
export const SODIUM_MG_PER_G_SALT = 387;
/** Same entry: 1 teaspoon weighs 6.1 g. */
export const SALT_G_PER_TSP = 6.1;
/** Adults' fast-food meals averaged 1,292 mg sodium, measured from receipts (Moran 2017). */
export const RESTAURANT_MEAL_SODIUM_MG = 1300;
export const COOKING_SALT_TSP = 0.25;
/** Largest single entry accepted, about 4 tsp of salt. */
export const MAX_SALT_ENTRY_MG = 10000;

export type SaltChoice = 'restaurant' | 'cooking' | 'custom';

export const SALT_CITATIONS = [
  { key: 'moran2017', text: 'Moran AJ, Ramirez M, Block JP. Consumer underestimation of sodium in fast food restaurant meals: results from a cross-sectional observational study. Appetite. 2017;113:155-161.', url: 'https://pubmed.ncbi.nlm.nih.gov/28235618/' },
  { key: 'harnack2017', text: 'Harnack LJ, Cogswell ME, Shikany JM, et al. Sources of sodium in US adults from 3 geographic regions. Circulation. 2017;135(19):1775-1783.', url: 'https://pubmed.ncbi.nlm.nih.gov/28483828/' },
  { key: 'fdc746775', text: 'USDA FoodData Central, Foundation Foods: Salt, table, iodized (FDC ID 746775).', url: 'https://fdc.nal.usda.gov/food-details/746775/nutrients' },
] as const;

export function saltTspToSodiumMg(tsp: number): number {
  return Math.round(tsp * SALT_G_PER_TSP * SODIUM_MG_PER_G_SALT);
}

/** Sodium (mg) for a choice, or null when a custom amount is missing or out of range. */
export function saltSodiumMg(choice: SaltChoice, customMg?: number | null): number | null {
  if (choice === 'restaurant') return RESTAURANT_MEAL_SODIUM_MG;
  if (choice === 'cooking') return saltTspToSodiumMg(COOKING_SALT_TSP);
  if (customMg === null || customMg === undefined || !Number.isFinite(customMg)) return null;
  const mg: number = Math.round(customMg);
  return mg >= 1 && mg <= MAX_SALT_ENTRY_MG ? mg : null;
}

export interface SaltEntryInput {
  choice: SaltChoice;
  /** Sodium in mg, used when choice is 'custom'. */
  customMg?: number | null;
  mealType: MealType;
  localDate: string;
  eatenAt: string;
}

export function saltMealInsert(input: SaltEntryInput): MealInsert | null {
  const mg: number | null = saltSodiumMg(input.choice, input.customMg);
  if (mg === null) return null;
  const saltG: number = Math.round((mg / SODIUM_MG_PER_G_SALT) * 10) / 10;
  const amount = input.choice === 'cooking' ? { quantity: COOKING_SALT_TSP, unit: 'tsp' }
    : input.choice === 'restaurant' ? { quantity: 1, unit: 'meal' }
      : { quantity: saltG, unit: 'g' };
  const item: MealItem = {
    name: input.choice === 'restaurant' ? 'Salt, restaurant or takeout (estimate)' : 'Salt, table',
    ...amount, grams: saltG,
    calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, sodium_mg: mg,
  };
  const what: string = input.choice === 'restaurant' ? ': restaurant or takeout meal' : input.choice === 'cooking' ? ': 1/4 tsp in cooking' : '';
  return {
    eaten_at: input.eatenAt, local_date: input.localDate, meal_type: input.mealType,
    description: `Added salt${what} (${mg.toLocaleString('en-US')} mg sodium)`,
    source: 'manual',
    calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0,
    fiber_g: null, sugar_g: null, alcohol_g: null, sodium_mg: mg, potassium_mg: null,
    items: [item], nutrients: basicItemNutrients(item), ai_model: null, ai_confidence: null,
  };
}
